import React, { useState } from 'react';
import { Chip, IconButton, Menu, MenuItem, Divider, ListItemText } from '@mui/material';
import { FaEllipsisV } from 'react-icons/fa';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { useEmployeePermissions } from '../../../hooks/useEmployeePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { personVerbsLockedBy } from '../../../constants/employmentSourceSystems';
import { resolveIndividualOpenMode } from '../../../utils/individualOpenMode';
import { INDIVIDUAL_PAGE_MODES } from '../../../constants/individualOpenModes';
import { COLORS } from '../../../constants/radar';
import { ORGANIZATIONAL_STATUSES, getStatusColor, getStatusTextColor, getStatusText } from '../../../constants/organizationalStatuses';
import { EMPLOYEE_STATUSES, getStatusColor as getEmployeeStatusColor, getStatusTextColor as getEmployeeStatusTextColor, getStatusText as getEmployeeStatusText } from '../../../constants/employeeStatuses';
import { formatTimeDuration } from '../../../utils/dateUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
import {
  EmployeeCard,
  EmployeeCardContent,
  EmployeeName,
  EmployeeRole,
  EmployeeDetails,
  ItalicText,
} from '../styles';
import { toLocalDate } from '../../../utils/dateOnly';

// ADR-113 §5 — the card menu is a LAUNCHER, not a second implementation.
// Since ADR-158 §7.4 it holds PERSON verbs only — Edit · Hire Withdraw ·
// Mark No Show · Rehire · Resign — plus Manage Contract, the route to the
// Contracts tab where every contract verb lives (Add/Change/Remove End Date,
// Cancel Contract, Cancel Scheduled Renewal, Replace Contract, Amend, and —
// the last one to leave the card — Start Delayed). No contract verb on the
// person card; no person verb on a contract card.
const EmployeeCardItem = ({ employee, onClick, onEdit, onView, onResign, onMarkNoShow, onHireWithdraw, onRehire, onManageContract }) => {
  const { canManageEmployee } = useEmployeePermissions();
  // ADR-183 D2 — person verbs end every contract: one sync-owned contract
  // locks them for non-admins (the Employment Information tab says why)
  const { user } = useAuth();
  const personVerbsLocked = Boolean(personVerbsLockedBy(employee, Boolean(user?.employee?.isAdmin)));
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditClick = () => {
    handleMenuClose();
    if (onEdit) onEdit(employee);
  };

  const handleResignClick = () => {
    handleMenuClose();
    if (onResign) onResign(employee);
  };

  const handleMarkNoShowClick = () => {
    handleMenuClose();
    if (onMarkNoShow) onMarkNoShow(employee);
  };

  const handleHireWithdrawClick = () => {
    handleMenuClose();
    if (onHireWithdraw) onHireWithdraw(employee);
  };

  const handleManageContractClick = () => {
    handleMenuClose();
    if (onManageContract) onManageContract(employee);
  };

  const handleRehireClick = () => {
    handleMenuClose();
    if (onRehire) onRehire(employee);
  };

  const handleCardClick = () => {
    // ADR-109 §1 — same rule as the reports' auto route and the Project page:
    // Edit when the user manages the individual and they are not resigned, otherwise View.
    if (resolveIndividualOpenMode({ employee, canManageEmployee }) === INDIVIDUAL_PAGE_MODES.EDIT) {
      if (onClick) onClick(employee);
    } else {
      if (onView) onView(employee);
    }
  };


  return (
    <EmployeeCard key={employee.id} onClick={handleCardClick}>
      <EmployeeCardContent>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <EmployeeAvatar employee={employee} size={48} />
              <EmployeeName>{employee.fullName}</EmployeeName>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '16px',
                backgroundColor: getStatusColor(employee.organizationalStatus),
                color: getStatusTextColor(employee.organizationalStatus),
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {getStatusText(employee.organizationalStatus)}
              </div>
              {canManageEmployee(employee) && (
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{ color: '#64748B' }}
                >
                  <FaEllipsisV />
                </IconButton>
              )}
            </div>
          </div>
          <EmployeeRole style={{ marginBottom: '8px' }}>
            {employee.position?.label || 'No position assigned'}
            {employee.grade?.label && ` • ${employee.grade.label}`}
          </EmployeeRole>
          {employee.timeInOrganization && (
            <ItalicText style={{ marginBottom: '8px' }}>
              {formatTimeDuration(employee.timeInOrganization, 'in the Organization')}
              {employee.timeInOrganization.years === 0 && 
               employee.timeInOrganization.months === 0 && 
               employee.timeInOrganization.days === 0 &&
               employee.organizationalStatus === ORGANIZATIONAL_STATUSES.JOINING && 
               employee.hireDate && 
               `, joining on ${dayjs.utc(employee.hireDate).format('MMM D, YYYY')}`}
            </ItalicText>
          )}
          {(employee.organizationalStatus === ORGANIZATIONAL_STATUSES.LEAVING ||
            employee.organizationalStatus === ORGANIZATIONAL_STATUSES.RESIGNED) &&
            employee.resignDate && (
            <ItalicText style={{ marginBottom: '8px' }}>
              {employee.organizationalStatus === ORGANIZATIONAL_STATUSES.LEAVING ? 'Leaving' : 'Left'} on {dayjs.utc(employee.resignDate).format('MMM D, YYYY')}
              {employee.resignReason && `, ${employee.resignReason}`}
            </ItalicText>
          )}
          {/* ADR-017: pending resignation outside the display window — neutral
              secondary line keeps the future date discoverable without the
              warning-colored Leaving treatment */}
          {employee.hasPendingResignation &&
            employee.organizationalStatus !== ORGANIZATIONAL_STATUSES.LEAVING &&
            employee.resignDate && (
            <ItalicText style={{ marginBottom: '8px' }}>
              Resignation scheduled for {dayjs.utc(employee.resignDate).format('MMM D, YYYY')}
            </ItalicText>
          )}
          {/* ADR-136 phase 6 (§15 slice 3): a scheduled replacement — sibling of
              the pending-resignation line above; cancellable until the cutover */}
          {employee.pendingRenewal?.effectiveOn && (
            <ItalicText style={{ marginBottom: '8px' }}>
              Contract renewal scheduled for {dayjs.utc(employee.pendingRenewal.effectiveOn).format('DD/MM/YYYY')}
            </ItalicText>
          )}
          {/* ADR-111 §6.4: a scheduled position/grade change — the card keeps
              showing today's position; this line makes the future change
              discoverable (covers position and grade-only changes alike) */}
          {employee.pendingPositionChange?.effectiveOn && (
            <ItalicText style={{ marginBottom: '8px' }}>
              Position/grade change scheduled for {dayjs.utc(employee.pendingPositionChange.effectiveOn).format('DD/MM/YYYY')}
            </ItalicText>
          )}
        </div>
        <EmployeeDetails>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '90px 1fr',
            rowGap: '12px',
            columnGap: '0px',
            alignItems: 'start'
          }}>
            <strong>Email:</strong>
            <div>{employee.email}</div>
            
            <strong>Location:</strong>
            <div>{employee.workingLocation?.name || 'Not specified'} {employee.cluster?.name && `• ${employee.cluster.name}`}</div>
            
            <strong>Contract:</strong>
            {/* ADR-156 §2/§6 — the flat fields are the PRIMARY contract's;
                when the person holds more than one, say so and send the
                reader to the Contracts tab rather than folding them here. */}
            <div>
              {employee.contractType?.name || 'Not specified'} • {employee.contractCompany?.name || 'Not specified'}
              {Array.isArray(employee.contracts) && employee.contracts.length > 1 && (
                <Chip
                  label={`+${employee.contracts.length - 1} contract${employee.contracts.length > 2 ? 's' : ''}`}
                  size="small"
                  title="This person holds more than one contract — see the Contracts tab"
                  sx={{ ml: 1, height: '20px', fontSize: '11px', backgroundColor: '#e0e7ff', color: '#4f46e5', fontWeight: 500 }}
                />
              )}
            </div>
            
            <strong>Representative:</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {employee.directManager ? (
                <>
                  <EmployeeAvatar employee={employee.directManager} size={24} />
                  <span>{employee.directManager.fullName}</span>
                </>
              ) : (
                <ItalicText>Not assigned</ItalicText>
              )}
            </div>
            
            <strong>Roles:</strong>
            <div>{employee.roles?.map(role => role.label).join(', ') || <ItalicText>No roles assigned</ItalicText>}</div>
            
            <strong>Projects:</strong>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {employee.projects?.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {employee.projects.map((project, index) => (
                    <span key={`${employee.id}-project-${project.id}-${index}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      {project.employeeData?.projectStatus && (
                        <Chip
                          label={getEmployeeStatusText(project.employeeData.projectStatus)}
                          size="small"
                          sx={{
                            backgroundColor: getEmployeeStatusColor(project.employeeData.projectStatus),
                            color: getEmployeeStatusTextColor(project.employeeData.projectStatus),
                            fontSize: '12px',
                            height: '20px'
                          }}
                        />
                      )}
                      <span>{project.name}</span>
                      {project.employeeData?.projectStatus === EMPLOYEE_STATUSES.JOINING && project.employeeData?.startDate && (
                        <span style={{ fontSize: '12px', fontStyle: 'normal', marginLeft: '0px' }}>
                          on {toLocalDate(project.employeeData.startDate)?.toLocaleDateString()}
                        </span>
                      )}
                      {project.employeeData?.projectStatus === EMPLOYEE_STATUSES.LEAVING && project.employeeData?.endDate && (
                        <span style={{ fontSize: '12px', fontStyle: 'normal', marginLeft: '0px' }}>
                          on {toLocalDate(project.employeeData.endDate)?.toLocaleDateString()}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <ItalicText>No projects assigned</ItalicText>
              )}
            </div>
          </div>
        </EmployeeDetails>
      </EmployeeCardContent>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem key="edit" onClick={handleEditClick}>
          <ListItemText
            primary={employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.RESIGNED ? 'View' : 'Edit'}
            secondary={employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.RESIGNED
              ? 'Open the record (read-only)'
              : 'Update the person’s details'}
            secondaryTypographyProps={{ fontSize: '12px' }}
          />
        </MenuItem>
        {/* ADR-113 §5 — the route to the contract-addressed actions: end
            dates, cancellations, renewals and amendments live on the
            contract's own sub-tab now, gated on that contract's state. */}
        <MenuItem key="manage-contract" onClick={handleManageContractClick}>
          <ListItemText
            primary="Manage Contract"
            secondary="End dates, renewals and amendments — on the Contracts tab"
            secondaryTypographyProps={{ fontSize: '12px' }}
          />
        </MenuItem>
        {(employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.JOINING
          || employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.LEAVING
          || employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.RESIGNED
          || employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.EMPLOYED)
          && (
          <Divider />
        )}
        {/* ADR-158 §7.4 — Start Delayed retired from the card (ADR-153
            decision 14): it is a contract verb (one contract's start moves;
            another joining contract's need not), it is not time-critical,
            and Manage Contract above is one click. Its only home is the
            contract's action row on the Contracts tab. */}
        {/* ADR-158 §7.1 — unchanged code, changed status: Hire Withdraw is
            not an ADR-113 §5.1 shortcut into a contract verb, it is a PERSON
            verb's home on the person's list surface. Withdrawing a hire voids
            every contract that has not started and the person becomes
            Cancelled; the gate is the person's status (the primary's,
            ADR-156 §2). Voiding ONE contract while the person keeps another
            is a different act — Void Contract, on the Contracts tab at
            ADR-144 phase 5 (ADR-159 §4 C5). */}
        {employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.JOINING && !personVerbsLocked && (
          <MenuItem key="hire-withdraw" onClick={handleHireWithdrawClick}>
            <ListItemText
              primary="Hire Withdraw"
              secondary="Cancels the hire — every contract that has not started is voided"
              secondaryTypographyProps={{ fontSize: '12px' }}
            />
          </MenuItem>
        )}
        {/* ADR-113 §5 — the contract-schedule verbs (Change End Date, Cancel
            Contract, Remove End Date) moved to the scheduled-end panel on the
            Contracts tab, where they sit beside the end they act on. Manage
            Contract above is the route. Their event-based gating (a Pending
            end exists — through the grace window, any status) carried over
            per-contract (ADR-113 §9.2). */}
        {/* ADR-113 §5 (shortcut set revised 2026-08-27) — Replace Contract
            lives only on the Contracts tab now, in its contract's action row
            (Manage Contract above is the route). Its ADR-136 gating —
            EMPLOYED/LEAVING, hidden while a replacement is scheduled —
            carried over there per-contract. */}
        {/* ADR-113 §6/§9.1 — Cancel Scheduled Renewal moved onto the
            scheduled-replacement panel of the contract that carries it, on
            the Contracts tab — including an already-ended contract (the
            gapped regime): the §5.5 latest-ended view keeps the panel, and
            the cancel, reachable there. */}
        {(employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.RESIGNED
          || employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.CANCELLED)
          && (
          <MenuItem key="rehire" onClick={handleRehireClick}>
            <ListItemText
              primary="Rehire"
              secondary="Start a new contract for this former employee"
              secondaryTypographyProps={{ fontSize: '12px' }}
            />
          </MenuItem>
        )}
        {/* ADR-136 phase 3 (§2a settlement #6): Mark No Show gates on the
            backend-computed fact (started + inside the no-show window),
            independent of a scheduled contract end — a fixed-term hire who
            never showed up must still be markable.
            ADR-158 §7.1 — a PERSON verb, homed here (see Hire Withdraw
            above): a no-show is a fact about the human — they did not
            appear — so it voids every contract due to start. */}
        {employee?.canMarkNoShow && !personVerbsLocked && (
          <MenuItem key="mark-no-show" onClick={handleMarkNoShowClick}>
            <ListItemText
              primary="Mark No Show"
              secondary="The person never started — every contract due to start is voided"
              secondaryTypographyProps={{ fontSize: '12px' }}
            />
          </MenuItem>
        )}
        {/* ADR-017: an outside-window pending resignation displays as employed —
            block Resign while a resignation/contract end is already pending
            (changing that end is the contract-schedule verb, not a second resign) */}
        {/* ADR-113 §3 — Add End Date moved to the contract's action row on
            the Contracts tab (its gates — no pending end, no scheduled
            replacement — re-derived from that contract's own facts). */}
        {/* ADR-149 §5.1 — Resign lives HERE, and no longer on the contract
            card. ADR-113 §4 called this a dual-home shortcut into a
            contract-addressed verb; §2 of ADR-149 shows that inference read
            "episode-addressed" (how the event is keyed) as "contract-
            addressed" (what the verb is about). A departure closes every
            contract the person holds, so it is a person verb — and this
            card was always a person surface. No code change; the gates and
            the shared modal are as they were. */}
        {employee?.organizationalStatus === ORGANIZATIONAL_STATUSES.EMPLOYED
          && !employee?.hasPendingResignation && !personVerbsLocked && (
          <MenuItem
            key="resign"
            onClick={handleResignClick}
            sx={{
              color: COLORS.DESTRUCTIVE,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <ListItemText
              primary="Resign"
              secondary="Schedule the end of employment on a chosen date"
              primaryTypographyProps={{ color: COLORS.DESTRUCTIVE }}
              secondaryTypographyProps={{ fontSize: '12px' }}
            />
          </MenuItem>
        )}
      </Menu>
    </EmployeeCard>
  );
};

export default EmployeeCardItem;